/**
 * Plain-function impersonation exit, callable from anywhere — including
 * the apiClient interceptor that lives outside React context.
 *
 * The Banner component has a richer version of this flow (it can do an
 * org-switch token exchange to land back on the right admin subdomain).
 * Auto-exit from the apiClient prefers the simpler path: read
 * `originalAdminData` from storage, restore it via localStorage so
 * AuthProvider picks it up on the next page load, then navigate to
 * the admin dashboard.  If that data is missing or corrupt, fall back
 * to /login.
 *
 * Triggered when a session-only impersonation token expires (15-min
 * TTL on `provider_launch.ts` patient-launch exchange) and the next
 * API call 401s.  Previously the apiClient just rejected silently and
 * the FE rendered the patient dashboard as empty — misleading because
 * the patient actually has data; the session just expired.
 */
import {queryClient} from '@/shared/contexts/QueryProvider';

export function exitImpersonationDueToExpiry(): void {
    if (typeof window === 'undefined') return;

    try {
        const originalAdminData =
            sessionStorage.getItem('originalAdminData') || localStorage.getItem('originalAdminData');

        if (!originalAdminData) {
            // No admin to restore — full logout.  Includes wiping the
            // session-only flag so the next mount doesn't try to use
            // the dead impersonation token.
            sessionStorage.removeItem('auth_session_only');
            sessionStorage.removeItem('user');
            window.location.href = '/login';
            return;
        }

        const parsed = JSON.parse(originalAdminData);
        if (!parsed?.userInfo || !parsed?.token) {
            localStorage.removeItem('originalAdminData');
            sessionStorage.removeItem('originalAdminData');
            sessionStorage.removeItem('auth_session_only');
            sessionStorage.removeItem('user');
            window.location.href = '/login';
            return;
        }

        const returnUser = {
            token: parsed.token,
            userInfo: {...parsed.userInfo, impersonate_id: undefined, isImpersonating: false},
            username: parsed.username,
            isLoggedIn: true,
            sessionOnly: false,
        };
        localStorage.setItem('user', JSON.stringify(returnUser));

        // Bust the /api/user/data query cache so returning to /admin and
        // re-impersonating doesn't render the prior patient's cached results.
        queryClient.removeQueries({queryKey: ['user-data']});

        sessionStorage.removeItem('auth_session_only');
        sessionStorage.removeItem('user');
        localStorage.removeItem('originalAdminData');
        sessionStorage.removeItem('originalAdminData');
        localStorage.removeItem('impersonationReturnUrl');
        sessionStorage.removeItem('impersonationReturnUrl');
        localStorage.removeItem('impersonationReturnOrigin');
        sessionStorage.removeItem('impersonationReturnOrigin');

        // Best-effort return URL — fall back to the admin dashboard.
        const returnUrl =
            sessionStorage.getItem('impersonationReturnUrl') ||
            localStorage.getItem('impersonationReturnUrl') ||
            '/admin?orgConsole=1';
        const safePath = returnUrl.startsWith('/admin') ? returnUrl : '/admin?orgConsole=1';
        window.location.href = safePath;
    } catch {
        sessionStorage.removeItem('auth_session_only');
        sessionStorage.removeItem('user');
        window.location.href = '/login';
    }
}
